import { containsIgnoreCase } from "../../utils/stringUtils";
import { getAdjCourt } from "../../utils/getAdjCourts";
import { formatYmd, parseLooseYmd, todayYmd } from "../../features/cases/dates";
import { ADJ_ACTIONS } from "../../features/cases/listActions";
import AddJCase from "../../components/Adjustice/AddJCase";
import VAdjCard from "../../components/Cards/VAdjCard";
import { Ellipsis, formatServerDate, parseServerDate } from "../cases/caseColumns";
import { SimpleStatusCell } from "../cases/CasesTable";
import UpcomingOverview from "../cases/UpcomingOverview";

const courtName = (c) => getAdjCourt(c?.court) ?? c?.court ?? "";
const dikasimosYmd = (c) => parseLooseYmd(c?.dikasimos);
// Placeholders like "-" count as empty (same rule as the Αστικά table).
const filled = (value) => (value?.trim().length > 2 ? value.trim() : "");
const hasResult = (c) => Boolean(filled(c.apotelesma) || filled(c.diataktiko));

const text = (key) => ({
  sortValue: (c) => c[key]?.trim() || "",
  render: (c) => <Ellipsis>{c[key]?.trim() || ""}</Ellipsis>,
});

const resultText = (key) => ({
  sortValue: (c) => filled(c[key]),
  render: (c) => <Ellipsis>{filled(c[key])}</Ellipsis>,
});

// kwdikos is a password for the court site: never a column.
const COLUMNS = [
  {
    id: "arkat",
    label: "Αρ. κατάθεσης",
    width: 120,
    defaultVisible: true,
    sortValue: (c) => c.arkat || "",
    render: (c) => <span className="ds-strong">{c.arkat}</span>,
  },
  { id: "note", flex: true, label: "Σημείωση", width: 220, defaultVisible: true, ...text("note") },
  {
    id: "court",
    flex: true,
    label: "Δικαστήριο",
    width: 150,
    defaultVisible: true,
    sortValue: courtName,
    render: (c) => <Ellipsis>{courtName(c)}</Ellipsis>,
  },
  {
    id: "dikasimos",
    label: "Δικάσιμος",
    width: 100,
    defaultVisible: true,
    sortValue: dikasimosYmd,
    render: (c) => {
      const ymd = dikasimosYmd(c);
      if (!ymd) return <Ellipsis>{c.dikasimos?.trim() || ""}</Ellipsis>;
      return (
        <span className={ymd >= todayYmd() ? "ds-date-upcoming" : "ds-muted"}>
          {formatYmd(ymd)}
        </span>
      );
    },
  },
  { id: "apotelesma", flex: true, label: "Αποτέλεσμα", width: 180, defaultVisible: true, ...resultText("apotelesma") },
  {
    id: "last_checked",
    label: "Τελ. έλεγχος",
    width: 120,
    defaultVisible: true,
    sortValue: (c) => parseServerDate(c.last_checked)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.last_checked)}</span>,
  },
  { id: "katigoria", flex: true, label: "Κατηγορία", width: 160, defaultVisible: false, ...text("katigoria") },
  { id: "antikeimeno", flex: true, label: "Αντικείμενο", width: 200, defaultVisible: false, ...text("antikeimeno") },
  { id: "tmima", label: "Τμήμα", width: 90, defaultVisible: false, ...text("tmima") },
  { id: "synthesi", label: "Σύνθεση", width: 90, defaultVisible: false, ...text("synthesi") },
  {
    id: "imnia_eisagwgis",
    label: "Ημ. εισαγωγής",
    width: 110,
    defaultVisible: false,
    sortValue: (c) => parseLooseYmd(c.imnia_eisagwgis),
    render: (c) => <span>{c.imnia_eisagwgis?.trim() || ""}</span>,
  },
  { id: "diataktiko", flex: true, label: "Διατακτικό", width: 200, defaultVisible: false, ...resultText("diataktiko") },
  {
    id: "created",
    label: "Καταχώρηση",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.created_at)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.created_at)}</span>,
  },
];

const search = (c, q) =>
  containsIgnoreCase(courtName(c), q) ||
  containsIgnoreCase(c.note, q) ||
  containsIgnoreCase(c.arkat, q) ||
  containsIgnoreCase(c.katigoria, q) ||
  containsIgnoreCase(c.antikeimeno, q);

const FILTER_KEYS = ["court", "katigoria", "result"];

const uniqueSorted = (values) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, "el"));

const getFilterDefs = (rows) => [
  {
    key: "court",
    label: "Δικαστήριο",
    allLabel: "Όλα",
    options: [...new Set(rows.map((c) => c.court).filter(Boolean))]
      .map((code) => ({ value: code, label: getAdjCourt(code) ?? code }))
      .sort((a, b) => a.label.localeCompare(b.label, "el")),
  },
  {
    key: "katigoria",
    label: "Κατηγορία",
    options: uniqueSorted(rows.map((c) => c.katigoria?.trim())).map((k) => ({
      value: k,
      label: k,
    })),
  },
  {
    key: "result",
    label: "Αποτέλεσμα",
    allLabel: "Όλες",
    options: [
      { value: "yes", label: "Με αποτέλεσμα" },
      { value: "no", label: "Χωρίς αποτέλεσμα" },
    ],
  },
];

const applyFilters = (rows, { court, katigoria, result }) =>
  rows.filter((c) => {
    if (court && c.court !== court) return false;
    if (katigoria && c.katigoria?.trim() !== katigoria) return false;
    if (result === "yes" && !hasResult(c)) return false;
    if (result === "no" && hasResult(c)) return false;
    return true;
  });

const title = (c) => (c?.note?.trim() ? c.note : c ? `Αρ. κατάθεσης ${c.arkat}` : "");
const subtitle = (c) =>
  c
    ? [c.note?.trim() ? `Αρ. κατάθεσης ${c.arkat}` : null, courtName(c)]
        .filter(Boolean)
        .join(" · ")
    : "";

const getEvents = (c) => {
  const ymd = dikasimosYmd(c);
  return ymd ? [{ ymd }] : [];
};

const Overview = ({ rows, onOpen }) => (
  <UpcomingOverview
    rows={rows}
    getEvents={getEvents}
    getLabel={(c) => ({ primary: title(c), secondary: subtitle(c) })}
    onOpen={onOpen}
    title="Επερχόμενες δικάσιμοι"
    stats={[
      { label: "υποθέσεις", value: rows.length },
      { label: "με αποτέλεσμα", value: rows.filter(hasResult).length },
      { label: "χωρίς έλεγχο ακόμη", value: rows.filter((c) => !c.last_checked).length },
    ]}
  />
);

const AddForm = ({ onClose, onAdded, allCases }) => (
  <AddJCase
    handleClickAddCaseModal={onClose}
    cases={allCases}
    handleAddCase={() => onAdded(null)}
  />
);

export const ADJUSTICE_CONFIG = {
  scope: "adjustice",
  title: "Διοικητικά",
  noun: { one: "υπόθεση", many: "υποθέσεις", this: "αυτή την υπόθεση" },
  searchPlaceholder: "Αναζήτηση (αρ. κατάθεσης, δικαστήριο, σημείωση)",
  addLabel: "Νέα υπόθεση",
  columns: COLUMNS,
  columnsById: Object.fromEntries(COLUMNS.map((c) => [c.id, c])),
  getRowLabel: (c) => `${c.arkat} ${courtName(c)}`,
  renderStatus: (c, pending) => <SimpleStatusCell singleCase={c} pending={pending} />,
  search,
  filterKeys: FILTER_KEYS,
  getFilterDefs,
  applyFilters,
  actions: ADJ_ACTIONS,
  bulkRecheck: false, // each check hits the court site; one at a time
  noteInPanel: true,
  pendingFilter: (entry) => entry?.category === "adj_check",
  refreshOnCheckDone: true,
  detail: {
    endpoints: {
      item: (id) => `/adjustice/${id}`,
      updates: (id) => `/adjupd/${id}`,
    },
    fullPageHref: (id) => `/adjustice/${id}`,
    getTitle: title,
    getSubtitle: subtitle,
    getRefreshKey: (c) => `${c?.last_checked ?? ""}|${c?.note ?? ""}`,
    documentTitle: (c) => `${title(c)} | ${courtName(c)} | MySolon.gr`,
    renderBody: ({ data, updates }) => (
      <VAdjCard key={data.id} variant="panel" singleCase={data} singleUpdates={updates} />
    ),
  },
  Overview,
  AddForm,
  modalClass: "ds-modal--addcase",
  afterAdd: { refreshDelays: [0] },
};
